import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useMutation } from "@apollo/client";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BULK_CREATE_APPLICATIONS } from "../graphql/mutations";

/**
 * BulkCreateApplications component allows users to create multiple applications of the same type at once
 *
 * @returns {JSX.Element} The BulkCreateApplications component
 */
const BulkCreateApplications = () => {
   const [applicationType, setApplicationType] = useState("");
   const [quantity, setQuantity] = useState(5);
   const [isCreating, setIsCreating] = useState(false);
   const [presets, setPresets] = useState({});

   // Function to load presets from localStorage
   const loadPresets = () => {
      const loadedPresets = {
         CSAW: JSON.parse(localStorage.getItem('csawPreset')),
         COV: JSON.parse(localStorage.getItem('covPreset')),
         PTPR: JSON.parse(localStorage.getItem('ptprPreset')),
         PLTP: JSON.parse(localStorage.getItem('pltpPreset')),
         PLTCP: JSON.parse(localStorage.getItem('pltcpPreset')),
         TCEBP: JSON.parse(localStorage.getItem('tcebpPreset'))
      };
      setPresets(loadedPresets);
   };

   // Load presets on component mount
   useEffect(() => {
      loadPresets();
   }, []);

   // Reload presets when application type changes to ensure we have the latest preset
   useEffect(() => {
      if (applicationType) {
         loadPresets();
      }
   }, [applicationType]);

   // Add window focus listener to refresh presets when returning to the tab/window
   useEffect(() => {
      const handleFocus = () => {
         loadPresets();
      };

      window.addEventListener('focus', handleFocus);

      return () => {
         window.removeEventListener('focus', handleFocus);
      };
   }, []);

   // Define application types with their labels and values
   const applicationTypes = [
      { value: "CSAW", label: "Chainsaw Registration (CSAW)" },
      { value: "COV", label: "Certificate of Verification (COV)" },
      { value: "PTPR", label: "Private Tree Plantation Registration (PTPR)" },
      { value: "PLTP", label: "Private Land Timber Permit (PLTP)" },
      { value: "PLTCP", label: "Private Land Timber Cutting Permit (PLTCP)" },
      { value: "TCEBP", label: "Tree Cutting/Earth Balling Permit (TCEBP)" }
   ];

   // Mutation for bulk creating applications
   const [bulkCreateApplications] = useMutation(BULK_CREATE_APPLICATIONS, {
      onCompleted: (data) => {
         toast.success(`Successfully created ${data.bulkCreateApplications.count} applications`, {
            description: `Application numbers: ${data.bulkCreateApplications.applicationNumbers.join(", ")}`
         });
         setIsCreating(false);
      },
      onError: (error) => {
         toast.error(`Failed to create applications: ${error.message}`);
         setIsCreating(false);
      }
   });

   // Handle form submission
   const handleSubmit = async (e) => {
      e.preventDefault();

      if (!applicationType) {
         toast.error("Please select an application type");
         return;
      }

      if (quantity <= 0 || quantity > 50) {
         toast.error("Quantity must be between 1 and 50");
         return;
      }

      setIsCreating(true);

      // Reload preset right before making the request to ensure we use the latest preset
      const latestPreset = JSON.parse(localStorage.getItem(`${applicationType.toLowerCase()}Preset`));

      try {
         await bulkCreateApplications({
            variables: {
               input: {
                  applicationType,
                  quantity: parseInt(quantity),
                  preset: latestPreset
               }
            }
         });
      } catch (error) {
         console.error("Error in bulk creation:", error);
         // Error is handled by the onError callback
      }
   };

   return (
      <Card>
         <CardHeader>
            <CardTitle>Bulk Create Applications</CardTitle>
            <CardDescription>
               Create multiple applications of the same type at once using saved presets
            </CardDescription>
         </CardHeader>
         <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
               <div className="space-y-2">
                  <Label htmlFor="applicationType">Application Type</Label>
                  <Select
                     value={applicationType}
                     onValueChange={setApplicationType}
                  >
                     <SelectTrigger id="applicationType">
                        <SelectValue placeholder="Select application type" />
                     </SelectTrigger>
                     <SelectContent>
                        {applicationTypes.map((type) => (
                           <SelectItem key={type.value} value={type.value}>
                              {type.label}
                           </SelectItem>
                        ))}
                     </SelectContent>
                  </Select>
                  {applicationType && !presets[applicationType] && (
                     <p className="text-xs text-amber-500">
                        No preset found for this application type. Default values will be used.
                     </p>
                  )}
                  {applicationType && presets[applicationType] && (
                     <p className="text-xs text-green-500">
                        Using saved preset for {applicationTypes.find(t => t.value === applicationType)?.label}
                     </p>
                  )}
               </div>

               <div className="space-y-2">
                  <Label htmlFor="quantity">Quantity (1-50)</Label>
                  <Input
                     id="quantity"
                     type="number"
                     min="1"
                     max="50"
                     value={quantity}
                     onChange={(e) => setQuantity(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                     Number of applications to create (maximum 50)
                  </p>
               </div>

               <Button
                  type="submit"
                  className="w-full"
                  disabled={isCreating || !applicationType}
               >
                  {isCreating ? (
                     <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating Applications...
                     </>
                  ) : (
                     "Create Applications"
                  )}
               </Button>
            </form>
         </CardContent>
      </Card>
   );
};

export default BulkCreateApplications;